import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNowStrict } from 'date-fns'
import { signalsAPI } from '../../services/api'
import type { ScanStatus } from '../../types'

const POLL_MS = 60_000
// The scanner runs hourly; ScanStatus carries no next-scan field, so the next
// run is derived from the last one plus that cadence.
const SCAN_INTERVAL_MS = 60 * 60 * 1000

/** "next in 42m", or "scanning now…" once that derived time is already past. */
const nextInLabel = (lastScan: Date, now: number): string => {
  const mins = Math.ceil((lastScan.getTime() + SCAN_INTERVAL_MS - now) / 60_000)
  return mins <= 0 ? 'scanning now…' : `next in ${mins}m`
}

/**
 * The slim, display-only line that replaced the engine console on the Signals
 * page: the scanner is alive, when it last ran, how many symbols it watches and
 * when it runs next. The "Engine settings →" link shows only for admins (users
 * who pass the live-trading gate); everyone else just reads the status.
 */
const EngineStatusRibbon = ({ isAdmin }: { isAdmin: boolean }) => {
  const [status, setStatus] = useState<ScanStatus | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    const load = async () => {
      try {
        const data = await signalsAPI.scanStatus()
        if (mounted.current) setStatus(data)
      } catch {
        // Keep the last reading; the line degrades to its base text.
      }
    }
    load()
    const timer = setInterval(() => {
      setNow(Date.now())
      load()
    }, POLL_MS)
    return () => {
      mounted.current = false
      clearInterval(timer)
    }
  }, [])

  const lastScan = status?.last_scan_at ? new Date(status.last_scan_at) : null

  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-terminal-border bg-terminal-surface px-3 py-2 text-xs text-gray-400"
      data-testid="engine-ribbon"
    >
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
      </span>
      <span>
        <span className="text-gray-300">Engine scanning automatically</span>
        {lastScan && (
          <>
            {' · '}last scan {formatDistanceToNowStrict(lastScan)} ago
            {' · '}
            {status!.symbols_scanned} symbols
            {' · '}
            {nextInLabel(lastScan, now)}
          </>
        )}
      </span>
      {isAdmin && (
        <Link
          to="/settings/engine"
          className="ml-auto font-medium text-accent hover:underline"
          data-testid="engine-settings-link"
        >
          Engine settings →
        </Link>
      )}
    </div>
  )
}

export default EngineStatusRibbon
